/**
 * BC Short Term Rental Registry Validation — Client Module
 *
 * Calls the `bc-registry-validate` Supabase Edge Function, which proxies
 * the BC Registry API server-side. API keys never reach the browser.
 *
 * When BC_REGISTRY_API_KEY is not set as a Supabase secret, the Edge Function
 * falls back to format-based simulation (H/PM/ST + 9 digits → active).
 *
 * Registration Number Formats (official):
 *   Host:               H  + 9 digits  e.g. H123456789
 *   Property Manager:   PM + 9 digits  e.g. PM123456789
 *   Strata Hotel Unit:  ST + 9 digits  e.g. ST123456789
 */

import { supabase } from "@/lib/supabase";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BCAddress {
  unitNumber?: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: "BC";
  country: "CA";
  postalCode: string;
}

export interface PermitValidationResult {
  isValid: boolean;
  identifier: string;
  /** Normalized status from the BC Registry API */
  status: "active" | "expired" | "suspended" | "not_found" | "error" | "coming_soon";
  /** ISO date string from `validUntil` */
  expiresAt?: string;
  address?: BCAddress;
  message?: string;
}

export interface AddressRequirements {
  isBusinessLicenceRequired: boolean;
  isPrincipalResidenceRequired: boolean;
  isStrProhibited: boolean;
  isStraaExempt: boolean;
  address?: BCAddress;
  message?: string;
}

// ─── Format validator (client-side, instant) ─────────────────────────────────

/** Returns true if the string matches the official BC STR registration format. */
export function isValidBCRegistrationFormat(identifier: string): boolean {
  return /^(H|PM|ST)\d{9}$/i.test(identifier?.trim() ?? "");
}

// ─── Validate Single Permit ──────────────────────────────────────────────────

/**
 * Validate a BC STR registration number via the `bc-registry-validate` Edge Function.
 * Falls back to format-based simulation when API credentials are not configured.
 */
export async function validatePermit(
  identifier: string,
  address?: BCAddress
): Promise<PermitValidationResult> {
  const id = identifier?.trim() ?? "";

  if (!id) {
    return {
      isValid: false,
      identifier: id,
      status: "not_found",
      message: "No registration number provided.",
    };
  }

  const { data, error } = await supabase.functions.invoke("bc-registry-validate", {
    body: { identifier: id, ...(address ? { address } : {}) },
  });

  if (error) {
    return {
      isValid: false,
      identifier: id,
      status: "error",
      message: error.message ?? "Validation request failed.",
    };
  }

  return data as PermitValidationResult;
}

// ─── Check Address Requirements ──────────────────────────────────────────────

/** Returns safe defaults — extend when BC Registry address API is available. */
export async function checkAddressRequirements(
  address: BCAddress
): Promise<AddressRequirements> {
  return {
    isBusinessLicenceRequired: true,
    isPrincipalResidenceRequired: true,
    isStrProhibited: false,
    isStraaExempt: false,
    address,
  };
}

/** Batch validate — not yet exposed in the UI. */
export async function submitBatchValidate(
  _records: Array<{ identifier: string; address?: BCAddress }>,
  _callBackUrl: string
): Promise<{ accepted: boolean; message: string }> {
  return { accepted: false, message: "Batch validation not yet available." };
}
